import { useEffect, useState } from 'react';
import { extractUrl } from './artifact-image';
import { inlineByteSize } from './artifact-file';
import { effectiveContent, parseFileRecordContent } from './file-record';
import type { WorkspaceFile } from './workspace-files';

/** The file's size to show in `FileDetailMeta`'s Size row: the inline byte size when we have the bytes in
 *  hand, else a best-effort remote size for a bare-URL artifact (no inline bytes to measure) via a
 *  Content-Length HEAD request. Stays `null` (Size row hidden) if the host blocks CORS / omits the
 *  header. Shared by the no-viewer fallback (`FileDetailBody`) and the viewer's own header
 *  (`DocViewerModal`/the standalone file page), which is why this lives as a hook rather than duplicated
 *  `useEffect`s. */
export function useFileSize(item: WorkspaceFile): number | null {
  const [remoteSize, setRemoteSize] = useState<number | null>(null);

  useEffect(() => {
    setRemoteSize(null);
    if (item.isApp || inlineByteSize(item) != null) return;
    // Compare against the header-stripped body, not the raw content — a header-bearing pointer
    // artifact's raw string is never equal to its own rawUrl, and a reference-backed one has no
    // content at all.
    const { body } = parseFileRecordContent(effectiveContent(item));
    const url = extractUrl(body);
    if (!url || body.trim() !== url) return;
    let cancelled = false;
    fetch(url, { method: 'HEAD' })
      .then((r) => {
        const len = r.headers.get('content-length');
        const n = len ? parseInt(len, 10) : NaN;
        if (!cancelled && Number.isFinite(n) && n > 0) setRemoteSize(n);
      })
      .catch(() => {
        /* CORS-blocked or no header — leave the Size row hidden */
      });
    return () => {
      cancelled = true;
    };
  }, [item]);

  return inlineByteSize(item) ?? remoteSize;
}
